import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, CampoInterruptor, CampoTexto, useAvisos, useFormulario, Ventana } from '@tallerya/ui';
import { useCambiosTipos, version, type Tipo, type ValoresTipo } from './datos';

// Alta y edición de un tipo de activo (#117), en una ventana: son tres datos.
// El código técnico no aparece: lo arma el sistema desde el nombre y no cambia.

export type EdicionTipo = 'nuevo' | 'editar' | null;

interface Props {
  edicion: EdicionTipo;
  /**
   * El tipo que se edita, **el vivo** y no una copia de cuando se abrió la
   * ventana: tras un conflicto la ficha se vuelve a pedir, y el siguiente
   * «Guardar» tiene que mandar la versión nueva.
   */
  ficha?: Tipo;
  onCerrar: () => void;
  /** Tras crear uno, para abrirlo y agregarle campos. */
  onCreado?: (id: string) => void;
}

export function VentanaTipoDeActivo({ edicion, ficha, onCerrar, onCreado }: Props) {
  // La clave reinicia el formulario al abrir otro tipo, o al volver a abrir. No
  // cambia con la versión: lo escrito se conserva tras un conflicto.
  const clave = edicion === null ? 'cerrada' : edicion === 'nuevo' ? 'nuevo' : String(ficha?.id);
  return (
    <Contenido
      key={clave}
      edicion={edicion}
      {...(ficha && { ficha })}
      onCerrar={onCerrar}
      {...(onCreado && { onCreado })}
    />
  );
}

function Contenido({ edicion, ficha: fichaViva, onCerrar, onCreado }: Props) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosTipos();
  const ficha = edicion === 'editar' ? (fichaViva ?? null) : null;

  const form = useFormulario<ValoresTipo>({
    inicial: {
      name: ficha?.name ?? '',
      identifierLabel: ficha?.identifierLabel ?? '',
      requiresIdentifier: ficha?.requiresIdentifier ?? false,
    },
    validar: (v) => ({
      ...(!v.name.trim() && { name: 'validation.required' }),
      ...(!v.identifierLabel.trim() && { identifierLabel: 'validation.required' }),
    }),
    guardar: (v) =>
      ficha ? cambios.editar(String(ficha.id), version(ficha.version), v) : cambios.crear(v, onCreado),
    camposPorCodigo: { 'asset_type.nombre_duplicado': 'name' },
  });

  async function guardar() {
    const nombre = form.valores.name.trim();
    if (!(await form.enviar())) return;
    avisar({
      titulo: ficha ? t('admin.tipos.aviso.guardado', { nombre }) : t('admin.tipos.aviso.creado', { nombre }),
    });
    onCerrar();
  }

  function cerrar() {
    if (form.guardando) return;
    onCerrar();
  }

  return (
    <Ventana
      abierta={edicion !== null}
      titulo={ficha ? t('admin.tipos.ventanaTipo.editar') : t('admin.tipos.nuevo')}
      info={ficha ? t('admin.tipos.ventanaTipo.editarTexto') : t('admin.tipos.ventanaTipo.nuevoTexto')}
      acciones={
        <>
          <Boton variante="texto" disabled={form.guardando} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton disabled={form.guardando} aria-busy={form.guardando} onClick={() => void guardar()}>
            {ficha ? t('admin.tipos.guardar') : t('admin.tipos.ventanaTipo.crear')}
          </Boton>
        </>
      }
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoTexto
        form={form}
        nombre="name"
        etiqueta={t('admin.tipos.nombre')}
        obligatorio
        placeholder={t('admin.tipos.nombreEjemplo')}
        autoComplete="off"
        ancho="completo"
      />
      <CampoTexto
        form={form}
        nombre="identifierLabel"
        etiqueta={t('admin.tipos.identificador')}
        obligatorio
        info={t('admin.tipos.identificadorAyuda')}
        placeholder={t('admin.tipos.identificadorEjemplo')}
        autoComplete="off"
        ancho="completo"
      />
      <CampoInterruptor
        form={form}
        nombre="requiresIdentifier"
        etiqueta={t('admin.tipos.exigeIdentificador')}
        info={t('admin.tipos.exigeIdentificadorAyuda')}
      />
    </Ventana>
  );
}
